import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from './Context/UserContext';
import MindteckLogo from '../assets/Mindteck Logo.png';
import axios from 'axios';

/* ────────────────────────────────────────────────────────────────────────────
   Icons — one set, one stroke weight, 18px, drawn from the same grid.
   ──────────────────────────────────────────────────────────────────────────── */
const svg = (children) => (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.75"
        strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
        {children}
    </svg>
);

const ICONS = {
    dashboard: svg(<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /></>),
    approval: svg(<><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>),
    finance: svg(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><path d="M12 18v-6" /><path d="M9.5 13.5h5" /></>),
    chart: svg(<><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" /></>),
    report: svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></>),
    edit: svg(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>),
    lock: svg(<><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>),
    newDoc: svg(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="12" y1="18" x2="12" y2="12" /><line x1="9" y1="15" x2="15" y2="15" /></>),
    userPlus: svg(<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></>),
    vendor: svg(<><path d="M3 9l1-5h16l1 5" /><path d="M4 9v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" /><path d="M3 9h18" /><path d="M9 21v-6h6v6" /></>),
    invoice: svg(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>),
    users: svg(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
    home: svg(<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>),
    shield: svg(<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>),
    terminal: svg(<><polyline points="4 17 10 11 4 5" /><line x1="12" y1="19" x2="20" y2="19" /></>),
    dot: svg(<circle cx="12" cy="12" r="3.5" />),
};

const getIcon = (name) => {
    const n = name?.toLowerCase() || '';
    if (n.includes('dashboard')) return ICONS.dashboard;
    if (n.includes('verifier approval')) return ICONS.approval;
    if (n.includes('finance')) return ICONS.finance;
    if (n.includes('vendor reports')) return ICONS.chart;
    if (n.includes('invoice reports')) return ICONS.report;
    if (n.includes('update data')) return ICONS.edit;
    if (n.includes('password')) return ICONS.lock;
    if (n.includes('create invoice') || n.includes('new invoice')) return ICONS.newDoc;
    if (n.includes('registration') || n.includes('vendor reg') || n.includes('sign up')) return ICONS.userPlus;
    if (n.includes('access') || n.includes('role') || n.includes('permission')) return ICONS.shield;
    if (n.includes('log')) return ICONS.terminal;
    if (n.includes('vendor') || n.includes('supplier')) return ICONS.vendor;
    if (n.includes('invoice')) return ICONS.invoice;
    if (n.includes('user')) return ICONS.users;
    if (n.includes('home')) return ICONS.home;
    return ICONS.dot;
};

/* ────────────────────────────────────────────────────────────────────────────
   Grouping — purely presentational. Every backend menu item still renders; an
   item that matches no rule falls into "More" so nothing is ever dropped.
   Groups with no items are not rendered at all, so each role sees only its own.
   ──────────────────────────────────────────────────────────────────────────── */
const GROUPS = [
    { id: 'overview', label: 'Overview', match: n => n.includes('dashboard') || n.includes('home') },
    { id: 'usersReview', match: n => n.includes('users review') || n.includes('user review') },
    { id: 'vendors', label: 'Vendors', match: n => (n.includes('vendor') || n.includes('supplier')) && !n.includes('report') && !n.includes('agreement') },
    { id: 'invoices', label: 'Invoices', match: n => n.includes('invoice') && !n.includes('report') },
    { id: 'approvals', label: 'Approvals', match: n => n.includes('approval') || n.includes('verifier') },
    { id: 'reports', label: 'Reports', match: n => n.includes('report') || n.includes('update data') },
    { id: 'admin', label: 'Administration', match: n => n.includes('role') || n.includes('access') || n.includes('user') || n.includes('log') || n.includes('password') || n.includes('agreement') },
];

const groupMenus = (items) => {
    const buckets = GROUPS.map(g => ({ ...g, items: [] }));
    const more = { id: 'more', label: 'More', items: [] };

    items.forEach(item => {
        const n = (item.MenuName || '').toLowerCase();
        const bucket = buckets.find(g => g.match(n));
        (bucket || more).items.push(item);
    });

    const out = buckets.filter(g => g.items.length > 0);
    if (more.items.length) out.push(more);
    return out;
};

/* ────────────────────────────────────────────────────────────────────────────
   Sidebar — brand, role/permission-driven grouped nav, footer.
   Access is still entirely backend-driven: only items in `user.menus` render.
   ──────────────────────────────────────────────────────────────────────────── */
export default function Sidebar({ collapsed = false, onToggleCollapse, onNavigate }) {
    const { user } = useUser();
    const location = useLocation();

    const [pendingCounts, setPendingCounts] = useState({});

    useEffect(() => {
        let isMounted = true;

        const fetchCounts = async () => {
            try {
                const empId = user?.empId;
                const res = await axios.get('/api/UpdateData/GetPendingCounts', {
                    params: empId ? { empId } : {}
                });
                if (isMounted) setPendingCounts(res.data || {});
            } catch (err) {
                if (isMounted) console.error('Pending counts error:', err);
            }
        };

        fetchCounts();
        const interval = setInterval(fetchCounts, 30000);

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [user]);

    const getCountForMenu = (menuName) => {
        const n = menuName?.toLowerCase() || '';
        const isVendorMenu = n.includes('vendor review') || n.includes('vendorapproval') || n.includes('vendor approval');
        const isInvoiceMenu = n.includes('invoice review') || n.includes('invoice approval');

        if (isVendorMenu && n.includes('po')) return pendingCounts.vendorReviewPO;
        if (isVendorMenu && n.includes('dept')) return pendingCounts.vendorReviewDeptHead;
        if (isVendorMenu && n.includes('finance')) return pendingCounts.vendorReviewFinance;
        if (isVendorMenu && n.includes('cfo')) return pendingCounts.vendorReviewCFO;
        if (isVendorMenu && (n.includes('verifier') || n.includes('verif'))) return pendingCounts.vendorReviewVerifier;

        if (isInvoiceMenu && n.includes('po')) return pendingCounts.invoiceReviewPO;
        if (isInvoiceMenu && n.includes('dept')) return pendingCounts.invoiceReviewDeptHead;
        if (isInvoiceMenu && n.includes('finance')) return pendingCounts.invoiceReviewFinance;
        if (isInvoiceMenu && n.includes('cfo')) return pendingCounts.invoiceReviewCFO;
        if (isInvoiceMenu && (n.includes('verifier') || n.includes('verif'))) return pendingCounts.invoiceReviewVerifier;
        if (n.includes('users review') || n.includes('user review')) return pendingCounts.usersReview;

        return undefined;
    };

    // Same de-duplication as before: one entry per distinct MenuLink.
    const allLinks = [...(user?.menus || [])].filter(m => m.MenuLink);

    // ---------------------------------------------------------
    // Petty Cash access
    // ALL logged-in users can create Petty Cash/Credit requests
    // ---------------------------------------------------------
    const hasPettyCash = allLinks.some(
        m =>
            m.MenuLink?.toLowerCase() === "/fillpettycashcredit" ||
            m.MenuName?.toLowerCase().includes("petty cash") ||
            m.MenuName?.toLowerCase().includes("pettycash")
    );

    if (!hasPettyCash) {
        allLinks.push({
            Id: "petty-cash-menu",
            MenuName: "Fill Petty Cash/Credit",
            MenuLink: "/FillPettyCashCredit"
        });
    }

    // ---------------------------------------------------------
    // Petty Cash DM Approval
    // Sandeep (IKE128) + Santosh (IBE1434)
    // ---------------------------------------------------------

    const currentEmpId = String(user?.empId || "")
        .trim()
        .toUpperCase();

    const deliveryManagerIds = ["IKE128", "IBE1434"];

    const isDeliveryManager = deliveryManagerIds.includes(currentEmpId);

    console.log("Petty Cash Debug:", {
        empId: user?.empId,
        currentEmpId,
        isDeliveryManager
    });

    if (isDeliveryManager) {
        allLinks.push({
            Id: "petty-cash-dm-approval",
            MenuName: "Petty Cash DM Approval",
            MenuLink: "/PettyCashDMApproval"
        });
    }





    const uniqueLinks = [];
    const seenLinks = new Set();
    allLinks.forEach(m => {
        if (!seenLinks.has(m.MenuLink)) {
            seenLinks.add(m.MenuLink);
            uniqueLinks.push(m);
        }
    });

    const groups = groupMenus(uniqueLinks);

    const renderItem = (item) => {
        const isActive = location.pathname.toLowerCase() === item.MenuLink?.toLowerCase();
        const count = getCountForMenu(item.MenuName); 
        return (
            <li key={item.Id ?? item.MenuLink}>
                <div
                    className={`menu-item${isActive ? ' active' : ''}`}
                    data-label={item.MenuName}
                >
                    <span className="menu-icon">{getIcon(item.MenuName)}</span>
                    <Link
                        to={item.MenuLink}
                        className="menu-link"
                        onClick={onNavigate}
                        aria-current={isActive ? 'page' : undefined}
                    >
                        {item.MenuName}
                    </Link>
                    {!!count && <span className="menu-badge">{count}</span>}
                </div>
            </li>
        );
    };

    return (
        <aside className="vms-sidebar" aria-label="Main navigation">
            <div className="side-brand">
                <Link to="/master-page" aria-label="Mindteck VMS dashboard">
                    <img src={MindteckLogo} alt="Mindteck" className="side-brand-logo" />
                </Link>
                <button
                    type="button"
                    className="side-collapse-btn"
                    onClick={onToggleCollapse}
                    aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
                    title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="3" y="4" width="18" height="16" rx="2" />
                        <line x1="9" y1="4" x2="9" y2="20" />
                    </svg>
                </button>
            </div>

            <nav className="side-nav" aria-label="Sections">
                {groups.length === 0 ? (
                    <p className="side-empty">
                        No menu items are assigned to your role yet. Ask your VMS
                        administrator to grant access.
                    </p>
                ) : (
                    groups.map(group => (
                        <div className="side-group" key={group.id}>
                            <span className="side-group-label">{group.label}</span>
                            <ul className="side-sublist">
                                {group.items.map(renderItem)}
                            </ul>
                        </div>
                    ))
                )}
            </nav>

            <div className="side-foot">
                <p className="side-foot-name">Mindteck</p>
                <p>A.M.R. Tech Park, Bangalore &ndash; 560068</p>
                <p className="vms-num">080 41548000</p>
                <a href="https://mindteck.com" target="_blank" rel="noreferrer">mindteck.com</a>
            </div>
        </aside>
    );
}
